/**
 * NavNode — componente recursivo del Sidebar.
 *
 * Extraído a su propio archivo para evitar la recursión infinita del plugin
 * `visual-edits/babel-metadata-plugin.js` cuando NavNode se auto-referencia
 * dentro del mismo archivo que Sidebar. La lógica se mantiene idéntica.
 *
 *   depth=0  → ítem raíz (padding/tipografía mayor)
 *   depth>=1 → sub-ítem (indentación creciente + tipografía menor)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

const NavNode = ({
  item,
  depth,
  location,
  expandedMenus,
  toggleMenu,
  handleNavClick,
  getIconComponent,
}) => {
  // Alias local: el runtime es idéntico (misma función), pero el binding
  // del elemento JSX ya no apunta directo a `NavNode` en el scope del
  // módulo. Esto evita que el plugin `visual-edits/babel-metadata-plugin`
  // resuelva la auto-referencia y entre en recursión infinita al analizar
  // el árbol de JSX.
  // eslint-disable-next-line no-use-before-define
  const RenderChild = NavNodeImpl;
  const Icon = getIconComponent(item.icon);
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = !!expandedMenus[item.id];

  // Detección recursiva de descendiente activo (cualquier nivel)
  const containsActive = (node) => {
    if (node.path && (location.pathname === node.path || location.pathname.startsWith(node.path + '/'))) {
      return true;
    }
    return (node.children || []).some(containsActive);
  };
  const hasActiveDescendant = hasChildren && item.children.some(containsActive);
  const isActive = !!(item.path && (location.pathname === item.path || location.pathname.startsWith(item.path + '/')));

  // Estilos por profundidad (compactado · 12-06-2026)
  const padLeftBase = depth === 0 ? 'px-2.5 lg:px-3' : '';
  const indentStyle = depth > 0 ? { paddingLeft: `${0.6 + depth * 0.7}rem` } : undefined;
  const sizeClass = depth === 0
    ? 'py-1.5 lg:py-2 text-sm'
    : 'py-1 lg:py-1.5 text-xs lg:text-[13px]';
  const iconSize = depth === 0 ? 17 : 15;

  return (
    <div data-testid={`nav-item-${item.id}`}>
      {item.path ? (
        <Link
          to={item.path}
          onClick={handleNavClick}
          data-testid={`nav-${item.path}`}
          style={indentStyle}
          className={`skin-nav-item flex items-center gap-2.5 ${padLeftBase} pr-3 rounded-md ${sizeClass} ${
            isActive ? 'selected-leaf' : ''
          }`}
        >
          <Icon size={iconSize} className={depth === 0 ? 'lg:w-[18px] lg:h-[18px]' : 'lg:w-[16px] lg:h-[16px]'} />
          <span className="truncate">{item.label}</span>
        </Link>
      ) : (
        <button
          onClick={() => toggleMenu(item.id)}
          data-testid={`nav-toggle-${item.id}`}
          style={indentStyle}
          className={`skin-nav-item w-full flex items-center justify-between ${padLeftBase} pr-3 rounded-md ${sizeClass} ${
            hasActiveDescendant ? 'active-parent' : ''
          }`}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Icon size={iconSize} className={depth === 0 ? 'lg:w-[18px] lg:h-[18px]' : 'lg:w-[16px] lg:h-[16px]'} />
            <span className="truncate">{item.label}</span>
          </div>
          {hasChildren && (
            isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />
          )}
        </button>
      )}

      {hasChildren && isExpanded && (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <RenderChild
              key={child.id}
              item={child}
              depth={depth + 1}
              location={location}
              expandedMenus={expandedMenus}
              toggleMenu={toggleMenu}
              handleNavClick={handleNavClick}
              getIconComponent={getIconComponent}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Referencia estable al mismo componente vía otro nombre. En runtime
// `NavNodeImpl === NavNode`, pero el analizador estático del plugin
// visual-edits no sigue la asignación → se rompe la recursión.
const NavNodeImpl = NavNode;

export default NavNode;
